import type {
  Ability,
  CharacterStats,
  MovementStats,
} from '../../CharacterTypes';

// Warlock — cloth caster built around DoT-stacking, curses, and self-sustain
// (life-tap-style HP-for-mana). Closest existing profile is Mage (also a cloth
// caster), but Warlock skews toward sustained DoT damage + lifesteal rather
// than Mage's burst/CC identity: higher baseline magicAttack (DoTs scale with
// spell power), lower criticalRate (DoTs aren't crit-dependent), slightly
// higher defense + health multiplier (self-sustain theme). The legacy
// `[warlock_classic]` reference base_stats (high strength/agility, low intellect) are
// legacy import data on a different scale — translated here to H2000's stat
// shape as a proper low-strength, high-intellect cloth caster.
export const WARLOCK_CONFIG = {
  name: 'Pactbound',
  description: 'Dark spellcaster who spreads corruption, curses, and steals life to sustain itself',
  baseStats: {
    strength: 6,
    dexterity: 7,
    intelligence: 17,
    luck: 12,
    attack: 14,
    magicAttack: 26,
    defense: 8,
    magicDefense: 14,
    criticalRate: 0.05
  } as CharacterStats,
  healthMultiplier: 0.9,
  manaMultiplier: 1.3,
  color: '#7D3C98',
  movementStats: {
    jumpPower: 1100,
    targetJumpHeight: 200,
    moveSpeed: 300,
    gravityScale: 0.9,
    terminalVelocity: 1000,
    acceleration: 3000,
    deceleration: 6000
  } as MovementStats,
  abilities: [
    {
      id: 'warlock_auto',
      name: 'Shadow Bolt',
      damage: 10,
      manaCost: 0,
      cooldown: 600,
      lastUsed: 0,
      range: 500,
      castTime: 0,
      description: 'Basic shadow bolt — quick ranged auto attack.',
      icon: '/icons/abilities/warlock_auto.svg'
    },
    {
      id: 'corruption',
      name: 'Corruption',
      damage: 8,
      manaCost: 12,
      cooldown: 3000,
      lastUsed: 0,
      range: 600,
      castTime: 0,
      description: 'Infect the target with a creeping shadow DoT.',
      icon: '/icons/abilities/corruption.svg'
    },
    {
      id: 'immolate',
      name: 'Immolate',
      damage: 12,
      manaCost: 15,
      cooldown: 5000,
      lastUsed: 0,
      range: 600,
      castTime: 0,
      description: 'Engulf the target in fel fire, burning over time.',
      icon: '/icons/abilities/immolate.svg'
    },
    {
      id: 'curse_of_agony',
      name: 'Curse of Agony',
      damage: 6,
      manaCost: 10,
      cooldown: 4000,
      lastUsed: 0,
      range: 600,
      castTime: 0,
      description: 'A worsening curse that ramps damage over its duration.',
      icon: '/icons/abilities/curse_of_agony.svg'
    },
    {
      id: 'life_tap',
      name: 'Life Tap',
      damage: 0,
      manaCost: 0,
      cooldown: 8000,
      lastUsed: 0,
      range: 0,
      castTime: 0,
      description: 'Sacrifice health to restore mana.',
      icon: '/icons/abilities/life_tap.svg'
    },
    {
      id: 'drain_life',
      name: 'Drain Life',
      damage: 18,
      manaCost: 20,
      cooldown: 10000,
      lastUsed: 0,
      range: 500,
      castTime: 0,
      description: 'Channel shadow energy to damage a target and heal yourself.',
      icon: '/icons/abilities/drain_life.svg'
    }
  ] as Ability[]
};
